import * as THREE from 'three';

// Owns the renderer and swaps between Scene objects (town, house, comics, ...).
// Each scene is a class with: enter(ctx), exit(), update(dt), and a `.scene`
// (THREE.Scene) + `.camera`. Transitions fade through a full-screen overlay.

export class SceneManager {
  constructor(canvas){
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scenes = new Map();     // id -> factory function () => Scene
    this.current = null;
    this.currentId = null;
    this.transitioning = false;

    this.clock = new THREE.Clock();
    this._buildOverlay();

    window.addEventListener('resize', () => this._onResize());
  }

  register(id, factory){ this.scenes.set(id, factory); }

  _buildOverlay(){
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed', inset: '0', background: '#6E4526',
      opacity: '0', pointerEvents: 'none', transition: 'opacity .35s ease',
      zIndex: '50'
    });
    document.body.appendChild(el);
    this.overlay = el;
  }

  // Switch scenes with a fade. `payload` is passed to the new scene's enter().
  async goTo(id, payload = {}){
    if (this.transitioning) return;
    this.transitioning = true;

    await this._fade(1);

    if (this.current && this.current.exit) this.current.exit();

    const factory = this.scenes.get(id);
    // Fade back before bailing — otherwise the overlay stays opaque and the
    // player is soft-locked on a brown screen with no way out.
    if (!factory){
      console.error('no scene registered:', id);
      await this._fade(0);
      this.transitioning = false;
      return;
    }

    try {
      this.current = factory();
      this.currentId = id;
      this.current.enter({ manager: this, payload });
    } catch (err) {
      console.error(`scene ${id} failed to load:`, err);
      this.current = null;
      this._showError(`Couldn't load "${id}"`, err);
    }

    // Always fade the (brown) transition overlay back out — even on error — so a
    // failed scene never leaves the player staring at a solid brown screen.
    await this._fade(0);
    this.transitioning = false;
  }

  // Visible, readable error banner (so failures aren't a silent blank/brown page).
  _showError(title, err){
    let el = document.getElementById('sceneError');
    if (!el){
      el = document.createElement('div');
      el.id = 'sceneError';
      Object.assign(el.style, {
        position: 'fixed', left: '50%', top: '14px', transform: 'translateX(-50%)',
        maxWidth: '92vw', background: '#FFF4DF', color: '#6B4A2F',
        border: '3px solid #6B4A2F', borderRadius: '14px', padding: '12px 16px',
        font: '13px/1.45 system-ui, sans-serif', zIndex: '200', whiteSpace: 'pre-wrap',
        boxShadow: '0 6px 0 rgba(107,74,47,.35)'
      });
      document.body.appendChild(el);
    }
    const msg = (err && (err.stack || err.message)) || String(err);
    el.textContent = `⚠️ ${title}\n${msg}`.slice(0, 700);
  }

  _fade(to){
    return new Promise(res => {
      this.overlay.style.opacity = String(to);
      setTimeout(res, 360);
    });
  }

  _onResize(){
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    if (this.current && this.current.onResize) this.current.onResize();
  }

  start(){
    const loop = () => {
      requestAnimationFrame(loop);
      const dt = Math.min(this.clock.getDelta(), 0.05);
      if (this.current){
        if (this.current.update) this.current.update(dt);
        this.renderer.render(this.current.scene, this.current.camera);
      }
    };
    loop();
  }
}
